import React, { useEffect, useRef, useState } from 'react';
import { useSelector } from 'react-redux';
import { getOnboardingCategories, uploadOnboardingFile, submitOnboardingForm, updateOnboardingForm } from '../services/onboardingService';
import styles from '../styles/OnboardingForm.module.css';
import { showToast } from '../utils/toast';
import { parseValidationErrors, summarizeValidationErrors, humanizeFieldName } from '../utils/validationErrors';

// Field names as they appear on screen, so a message about `brand_poc_number`
// can say "Brand POC Number". Mirrors the labels the API sends back.
const FIELD_LABELS = {
  brand_name: 'Brand Name',
  brand_poc_name: 'Brand Point of Contact (POC) Name',
  brand_poc_number: 'Brand POC Number',
  email_address: 'Email Address',
  general_contact_number: 'General Contact Number',
  company_name: 'Company Name',
  company_address: 'Company Address',
  billing_address: 'Billing Address',
  general_processing_timeline: 'Order Processing Timeline',
  country_of_origin: 'Country of Origin',
  warehouse_poc_name: 'Warehouse POC Name',
  warehouse_poc_number: 'Warehouse POC Number',
  warehouse_poc_address: 'Warehouse POC Address',
  operations_poc_name: 'Operations POC Name',
  operations_poc_number: 'Operations POC Number',
  operations_poc_email: 'Operations POC Email',
  dispatch_timelines: 'Dispatch Timelines',
  gstin: 'GSTIN',
  gstin_certificate: 'GSTIN Certificate',
  cancelled_cheque: 'Cancelled Cheque',
  bank_name: 'Bank Name',
  bank_account_no: 'Account Number',
  bank_account_type: 'Account Type',
  bank_ifsc: 'IFSC Code',
  account_holder_name: 'Account Holder Name',
  bank_branch: 'Bank Branch',
  manufacturing_country: 'Manufacturing Country',
  manufacturer_name: 'Manufacturer Name',
  manufacturer_address: 'Manufacturer Address',
  packer_name: 'Packer Name',
  packer_address: 'Packer Address',
  importer_of_record: 'Importer of Record (IOR)',
  import_license_number: 'Import License Number',
  suspire_category: 'Suspire Category',
  accepted_terms: 'Terms & Conditions',
};

// Sample values shown alongside "is required" messages, so it is obvious what
// the field expects rather than just that it is empty.
const FIELD_EXAMPLES = {
  brand_name: 'Aurelia Home',
  brand_poc_name: 'Priya Sharma',
  brand_poc_number: '9876543210',
  email_address: 'hello@yourbrand.com',
  general_contact_number: '9876543210',
  company_name: 'Aurelia Home Private Limited',
  company_address: '12 MG Road, Bengaluru, Karnataka 560001',
  billing_address: '12 MG Road, Bengaluru, Karnataka 560001',
  general_processing_timeline: '2-3 business days',
  country_of_origin: 'India',
  warehouse_poc_name: 'Rahul Verma',
  warehouse_poc_number: '9876543210',
  warehouse_poc_address: '12 MG Road, Bengaluru, Karnataka 560001',
  operations_poc_name: 'Rahul Verma',
  operations_poc_number: '9876543210',
  operations_poc_email: 'ops@yourbrand.com',
  gstin: '27AAPFU0939F1ZV',
  bank_name: 'HDFC Bank',
  bank_ifsc: 'HDFC0001234',
  manufacturing_country: 'India',
};


export default function OnboardingForm({ onFormSubmitted, initialData, isEditMode = false, isViewMode = false, onEdit, onCancel, onSave }) {
  const token = useSelector(state => state.user.token);
  const onboarding = useSelector(state => state.user.onboarding);
  const [form, setForm] = useState(initialData || onboarding.form_data || {});
  const [categories, setCategories] = useState([]);
  const [saving, setSaving] = useState(false);
  const [saved, setSaved] = useState(false);
  const [error, setError] = useState(null);
  const [fileUploading, setFileUploading] = useState({});
  const [fileError, setFileError] = useState(null);
  const [validationErrors, setValidationErrors] = useState({});
  const [apiErrors, setApiErrors] = useState({}); // Field -> message, from the server
  // Full detail of what the server rejected: per-field messages grouped by the
  // section of the form they belong to, so the banner can explain each one.
  const [apiErrorDetail, setApiErrorDetail] = useState(null);
  const [summaryDismissed, setSummaryDismissed] = useState(false);
  const formRef = useRef(null);

  // Load categories and initialize form with API data
  useEffect(() => {
    if (token) {
      getOnboardingCategories(token).then(data => {
        setCategories(data);
      }).catch(err => {
        console.error('Error loading categories:', err);
        setCategories([]);
      });
    }
  }, [token]);

  // Helper to flatten nested API data into form structure
  const parseApiData = (data) => {
    if (!data) return {};

    // Check if data is already flattened (has brand_name at top level) or nested (has brand object)
    const isFlattened = data.brand_name !== undefined && data.brand === undefined;

    if (isFlattened) {
      // Data is already flattened, return as-is
      return { ...data };
    }

    // Deep copy to avoid mutating original
    const flat = {};

    // 1. Brand Info
    if (data.brand) {
      flat.brand_name = data.brand.name || '';
      flat.brand_logo_url = data.brand.logo_url || data.brand.logo || '';
    }

    // 2. Contact Information (from contact_information object)
    if (data.contact_information) {
      Object.assign(flat, data.contact_information);
    }

    // 3. Legal & Financial
    if (data.legal_financial) {
      Object.assign(flat, data.legal_financial);
      // Map file IDs/URLs to form fields
      flat.gstin_certificate = data.legal_financial.gstin_certificate_file || '';
      flat.cancelled_cheque = data.legal_financial.cancelled_cheque_file || '';
      // Map file URLs for display
      flat.gstin_certificate_file_url = data.legal_financial.gstin_certificate_file_url || '';
      flat.cancelled_cheque_file_url = data.legal_financial.cancelled_cheque_file_url || '';
    }

    // 4. Manufacturing
    if (data.manufacturing) {
      Object.assign(flat, data.manufacturing);
      flat.manufacturer_excel = data.manufacturing.manufacturer_excel_file || '';
      // Map file URL for display
      flat.manufacturer_excel_file_url = data.manufacturing.manufacturer_excel_file_url || '';
    }

    // 5. Order Policy
    if (data.order_policy) {
      Object.assign(flat, data.order_policy);
    }

    // 6. Product Categorization
    if (data.product_categorization) {
      Object.assign(flat, data.product_categorization);
      // Map suspire_category specifically (it might be an ID or object)
      // The API example shows "suspire_category": "uuid...", so it's a string ID.
      // If it were an object {id: "...", name: "..."}, we'd need data.product_categorization.suspire_category.id
      flat.signed_agreement = data.product_categorization.signed_agreement_file || '';
      flat.certifications = data.product_categorization.certifications || '';
      // Map file URLs for display
      flat.signed_agreement_file_url = data.product_categorization.signed_agreement_file_url || '';
      flat.certifications_url = data.product_categorization.certifications_url || '';
    }

    // 7. Warehouses (Array -> single set of fields for first warehouse)
    if (data.warehouses && data.warehouses.length > 0) {
      Object.assign(flat, data.warehouses[0]);
    }

    // 8. Exchange Policy & Return Policy
    // API has "valid_on": [...] which might need mapping to boolean checkbox flags
    // But basic boolean "exchanges" or "returns" might prevent opening the section.
    // The API example shows "exchange_policy": {} empty.
    // If you have specific booleans like "exchange_damaged" inside, Object.assign handles them if keys match.
    // We need to set the parent toggles 'exchanges' and 'returns' based on presence of policy data

    // Logic: if exchange_policy has non-null keys or is not empty, set exchanges=true? 
    // Or check if any specific sub-field is true.
    // For now assuming the keys in `exchange_policy` match the form keys (e.g. exchange_damaged).
    if (data.exchange_policy) {
      Object.assign(flat, data.exchange_policy);
      // Heuristic: if we have policy details, enable the main toggle
      // flat.exchanges = Object.keys(data.exchange_policy).length > 0;
    }

    if (data.return_policy) {
      Object.assign(flat, data.return_policy);
      // flat.returns = Object.keys(data.return_policy).length > 0;
    }

    // Merge any top-level keys that might already be flat (e.g. from auto-save)
    // but give precedence to the structured sections we just parsed.
    // Actually, let's mix in the rest of 'data' *before* specific overrides if needed,
    // or just assume 'data' contains the top-level keys too.
    // Given the structure is nested, we shouldn't indiscriminately spread 'data' if it has 'brand': {...}

    return flat;
  };

  // Initialize form data
  // - If `initialData` is provided (edit mode), use it directly as it's already flattened
  // - Otherwise, fall back to onboarding.form_data from Redux and parse the nested structure
  useEffect(() => {
    if (initialData) {
      setForm(prev => ({ ...prev, ...initialData }));
    } else if (onboarding.form_data) {
      const parsed = parseApiData(onboarding.form_data);
      setForm(prev => ({ ...prev, ...parsed }));
    }
  }, [onboarding.form_data, initialData]);

  // Reset form to initial data when canceling edit mode
  useEffect(() => {
    if (isEditMode && isViewMode && initialData) {
      setForm(initialData);
      setValidationErrors({});
      setApiErrors({});
      setApiErrorDetail(null);
      setError(null);
    }
  }, [isViewMode, isEditMode, initialData]);

  // Helper function to normalize values for comparison (convert to strings, handle null/undefined)
  const normalizeValue = (value) => {
    if (value === null || value === undefined) return '';
    if (typeof value === 'boolean') return value;
    if (typeof value === 'number') return value;
    if (typeof value === 'string') return value.trim();
    if (Array.isArray(value)) return value.map(normalizeValue);
    if (typeof value === 'object') {
      const normalized = {};
      Object.keys(value).forEach(key => {
        if (value[key] !== undefined) {
          normalized[key] = normalizeValue(value[key]);
        }
      });
      return normalized;
    }
    return value;
  };

  // Helper function to deep compare two objects (ignoring functions and undefined values)
  const deepEqual = (obj1, obj2) => {
    const normalized1 = normalizeValue(obj1);
    const normalized2 = normalizeValue(obj2);

    if (normalized1 === normalized2) return true;
    if (normalized1 == null || normalized2 == null) return normalized1 === normalized2;
    if (typeof normalized1 !== 'object' || typeof normalized2 !== 'object') {
      return normalized1 === normalized2;
    }

    const keys1 = Object.keys(normalized1);
    const keys2 = Object.keys(normalized2);

    if (keys1.length !== keys2.length) return false;

    for (const key of keys1) {
      if (!keys2.includes(key)) return false;
      if (typeof normalized1[key] === 'function' || typeof normalized2[key] === 'function') continue;
      if (!deepEqual(normalized1[key], normalized2[key])) return false;
    }

    return true;
  };

  // Check if form has changes compared to initial data
  const hasFormChanges = () => {
    if (!isEditMode || !initialData || isReadOnly) return false;
    return !deepEqual(form, initialData);
  };

  // Clear validation error when user starts typing
  const handleChange = (e) => {
    const { name, value, type, checked } = e.target;
    const newValue = type === 'checkbox' ? checked : value;

    // Standard handling for all form fields
    setForm(prevForm => {
      let updatedForm = {
        ...prevForm,
        [name]: newValue
      };

      // Special logic for the "Manufacturer & Packer same" checkbox
      if (name === 'manufacturing_and_packer_same') {
        if (newValue) {
          // If "packer same" is checked, copy manufacturer details
          updatedForm.packer_name = prevForm.manufacturer_name || '';
          updatedForm.packer_address = prevForm.manufacturer_address || '';
        } else {
          // If unchecked, clear the packer fields
          updatedForm.packer_name = '';
          updatedForm.packer_address = '';
        }
      }

      return updatedForm;
    });

    // Clear validation errors when user starts typing
    if (validationErrors[name]) {
      setValidationErrors(prev => ({ ...prev, [name]: null }));
    }

    // Clear API errors when user starts typing
    if (apiErrors[name]) {
      setApiErrors(prev => ({ ...prev, [name]: null }));
      // Drop the field from the summary banner too, so the list shrinks as
      // problems get fixed rather than going stale.
      setApiErrorDetail(prev => {
        if (!prev) return prev;
        const issues = prev.issues.filter(issue => issue.field !== name);
        if (issues.length === 0) return null;
        return {
          ...prev,
          issues,
          count: issues.length,
          sections: prev.sections
            .map(section => ({ ...section, issues: section.issues.filter(i => i.field !== name) }))
            .filter(section => section.issues.length > 0),
        };
      });
    }
  };

  // Handle file upload
  const handleFileChange = async (e, file_type) => {
    const file = e.target.files[0];
    if (!file) return;

    // Logo uploads accept images, other files accept PDFs
    const isLogoUpload = file_type === 'brand_logo';
    const isValidFileType = isLogoUpload 
      ? file.type.startsWith('image/')
      : file.type === 'application/pdf';

    if (!isValidFileType) {
      setFileError(isLogoUpload ? 'Only image files allowed' : 'Only PDF files allowed');
      return;
    }
    if (file.size > 5 * 1024 * 1024) {
      setFileError('File size exceeds 5MB');
      return;
    }

    setFileUploading(prev => ({ ...prev, [file_type]: true }));
    setFileError(null);

    try {
      const res = await uploadOnboardingFile(token, file, file_type);
      // Update form with file ID and URL
      setForm(f => {
        const updated = { ...f };
        const fileUrl = res.file_url || res.url || '';
        const fileId = res.file_id || res.id || '';
        
        if (isLogoUpload) {
          // For logo, store URL in brand_logo_url
          updated.brand_logo_url = fileUrl;
        } else {
          // Store file ID (UUID) if available, otherwise use file_url
          updated[file_type] = fileId || fileUrl || '';
          
          // Map file URL to correct field name based on file type
          // Note: certifications uses 'certifications_url', not 'certifications_file_url'
          let urlField;
          switch (file_type) {
            case 'certifications':
              urlField = 'certifications_url';
              break;
            case 'manufacturer_excel':
              urlField = 'manufacturer_excel_file_url';
              break;
            case 'signed_agreement':
              urlField = 'signed_agreement_file_url';
              break;
            case 'gstin_certificate':
              urlField = 'gstin_certificate_file_url';
              break;
            case 'cancelled_cheque':
              urlField = 'cancelled_cheque_file_url';
              break;
            default:
              urlField = `${file_type}_file_url`;
          }
          
          // Store file URL for display
          updated[urlField] = fileUrl;
        }
        return updated;
      });
    } catch (err) {
      setFileError(`Upload failed: ${err.message}`);
    } finally {
      setFileUploading(prev => ({ ...prev, [file_type]: false }));
    }
  };

  // Form validation
  const validateForm = () => {
    const errors = {};
    const requiredFields = [
      'email_address', 'company_name', 'company_address', 'billing_address',
      'brand_poc_name', 'brand_poc_number', 'general_contact_number',
      'warehouse_poc_name', 'warehouse_poc_number', 'warehouse_poc_address',
      'operations_poc_name', 'operations_poc_number', 'operations_poc_email',
      'gstin', 'bank_name', 'bank_account_no', 'bank_account_type', 'bank_ifsc', 'account_holder_name',
      'suspire_category', 'general_processing_timeline', 'country_of_origin',
      'manufacturing_country', 'manufacturer_name', 'manufacturer_address'
    ];

    // Add packer fields if manufacturer and packer are not the same
    if (!form.manufacturing_and_packer_same) {
      requiredFields.push('packer_name', 'packer_address');
    }

    const labelFor = (field) => FIELD_LABELS[field] || humanizeFieldName(field);

    requiredFields.forEach(field => {
      if (!form[field] || (typeof form[field] === 'string' && form[field].trim() === '')) {
        const example = FIELD_EXAMPLES[field];
        errors[field] = `${labelFor(field)} is required and is currently empty.`
          + (example ? ` For example: ${example}.` : '');
      }
    });

    if (!form.accepted_terms) {
      errors.accepted_terms =
        'You need to accept the Terms & Conditions before submitting. Tick the box at the bottom of this form to confirm you accept the seller agreement.';
    }

    // Email validation
    const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
    const checkEmail = (field, example) => {
      const value = form[field];
      if (!value || emailRegex.test(value)) return;
      let reason = '';
      if (!value.includes('@')) {
        reason = " but it is missing the '@' symbol";
      } else if ((value.match(/@/g) || []).length > 1) {
        reason = " but it contains more than one '@' symbol";
      } else if (!value.split('@')[1] || !value.split('@')[1].includes('.')) {
        reason = " but the part after '@' is not a complete domain — it should look like 'company.com'";
      } else if (/\s/.test(value)) {
        reason = ' but it contains spaces, which are not allowed in an email address';
      }
      errors[field] =
        `${labelFor(field)} is not a valid email address. You entered '${value}'${reason}. `
        + `Please enter a complete address such as ${example}.`;
    };
    checkEmail('email_address', 'hello@yourbrand.com');
    checkEmail('operations_poc_email', 'ops@yourbrand.com');

    // Phone number validation (backend requires exactly 10 digits)
    const checkPhone = (field) => {
      const value = form[field];
      if (!value) return;
      const digits = String(value).replace(/\D/g, '');
      if (digits.length === 10 && /^\d{10}$/.test(String(value))) return;

      let reason;
      if (digits.length === 12 && digits.startsWith('91')) {
        reason = "it looks like you included the '+91' country code — please remove it and enter only the 10-digit number";
      } else if (digits.length === 11 && digits.startsWith('0')) {
        reason = "it starts with a leading '0' — please remove it";
      } else if (digits.length > 10) {
        reason = `it has ${digits.length - 10} digit(s) too many`;
      } else if (digits.length < 10) {
        reason = `it is ${10 - digits.length} digit(s) short`;
      } else {
        reason = 'it contains spaces or symbols, which are not allowed';
      }
      errors[field] =
        `${labelFor(field)} must be exactly 10 digits. You entered '${value}', which has ${digits.length} digit(s) `
        + `once spaces and symbols are removed. Problem: ${reason}. Enter the number without a country code or spaces, e.g. 9876543210.`;
    };
    ['brand_poc_number', 'general_contact_number', 'warehouse_poc_number', 'operations_poc_number'].forEach(checkPhone);

    // Address fields minimum length (backend requires at least 5 characters)
    const addressFields = ['company_address', 'billing_address', 'warehouse_poc_address', 'manufacturer_address'];
    if (!form.manufacturing_and_packer_same) {
      addressFields.push('packer_address');
    }
    addressFields.forEach(field => {
      if (form[field] && form[field].trim().length < 5) {
        errors[field] =
          `${labelFor(field)} must be at least 5 characters long, but you entered `
          + `${form[field].trim().length} ('${form[field].trim()}'). Include the street, city, state and PIN code.`;
      }
    });

    // GSTIN validation (15 alphanumeric characters: A-Z, 0-9)
    if (form.gstin && !/^[0-9A-Z]{15}$/.test(form.gstin)) {
      const value = form.gstin;
      const problems = [];
      if (value.length !== 15) {
        problems.push(value.length < 15
          ? `it is ${15 - value.length} character(s) too short`
          : `it is ${value.length - 15} character(s) too long`);
      }
      if (/[a-z]/.test(value)) problems.push('it contains lowercase letters — GSTIN must be in capitals');
      const invalid = [...new Set(value.split('').filter(c => !/[0-9A-Za-z]/.test(c)))];
      if (invalid.length) {
        problems.push(`it contains ${invalid.map(c => `'${c}'`).join(', ')} — only letters A–Z and digits 0–9 are allowed`);
      }
      errors.gstin =
        `GSTIN must be exactly 15 characters using only capital letters (A–Z) and digits (0–9). `
        + `You entered '${value}' (${value.length} characters)`
        + (problems.length ? `. Problem(s) found: ${problems.join('; and ')}` : '')
        + `. A valid GSTIN looks like 27AAPFU0939F1ZV — you can copy it from your GST registration certificate.`;
    }

    // Bank IFSC validation (4 letters + 0 + 6 digits)
    if (form.bank_ifsc && !/^[A-Z]{4}0\d{6}$/.test(form.bank_ifsc)) {
      const value = form.bank_ifsc;
      const problems = [];
      if (value.length !== 11) {
        problems.push(value.length < 11
          ? `it is ${11 - value.length} character(s) too short`
          : `it is ${value.length - 11} character(s) too long`);
      } else {
        if (!/^[A-Za-z]{4}/.test(value)) problems.push(`the first 4 characters ('${value.slice(0, 4)}') must be letters (the bank code)`);
        else if (value[4] !== '0') problems.push(`the 5th character must be the digit '0' but it is '${value[4]}'`);
        else if (!/^\d{6}$/.test(value.slice(5))) problems.push(`the last 6 characters ('${value.slice(5)}') must all be digits`);
      }
      if (/[a-z]/.test(value)) problems.push('it contains lowercase letters — IFSC codes must be in capitals');
      errors.bank_ifsc =
        `IFSC Code must be 11 characters: 4 letters, then the digit 0, then 6 digits. You entered '${value}'`
        + (problems.length ? `. Problem(s) found: ${problems.join('; and ')}` : '')
        + `. A valid IFSC looks like HDFC0001234 — it is printed on your cheque book and in your net-banking details.`;
    }

    // Bank account number validation (at least 6 digits, numeric only)
    if (form.bank_account_no && !/^\d{6,}$/.test(form.bank_account_no)) {
      const value = String(form.bank_account_no);
      const problems = [];
      const nonDigits = [...new Set(value.split('').filter(c => !/\d/.test(c)))];
      if (nonDigits.length) {
        problems.push(`it contains ${nonDigits.map(c => `'${c}'`).join(', ')} — an account number must contain digits only`);
      }
      const digits = value.replace(/\D/g, '');
      if (digits.length < 6) {
        problems.push(`it has only ${digits.length} digit(s), but at least 6 are needed`);
      }
      errors.bank_account_no =
        `Account Number must be digits only and at least 6 digits long`
        + (problems.length ? `. Problem(s) found: ${problems.join('; and ')}` : '')
        + `. Copy the account number exactly as it appears on your cancelled cheque, without spaces or dashes.`;
    }

    // Helper function to check if a file exists (either uploaded or from API)
    // A file is considered valid if:
    // 1. File ID exists (uploaded in current session or loaded from API)
    // 2. File URL exists (loaded from API response)
    const hasFile = (fileIdField, fileUrlField) => {
      const fileId = form[fileIdField];
      const fileUrl = form[fileUrlField];
      
      // Check file ID: must be truthy and non-empty after trimming
      const hasFileId = fileId && typeof fileId === 'string' && fileId.trim() !== '';
      
      // Check file URL: must be truthy and non-empty after trimming, and be a valid URL format
      const hasFileUrl = fileUrl && typeof fileUrl === 'string' && fileUrl.trim() !== '' && fileUrl.trim().startsWith('http');
      
      return hasFileId || hasFileUrl;
    };

    // File upload validation for GSTIN Certificate and Cancelled Cheque
    // Only validate if file doesn't exist (neither uploaded nor from API)
    if (!hasFile('gstin_certificate', 'gstin_certificate_file_url')) {
      errors.gstin_certificate =
        'GSTIN Certificate has not been uploaded yet, and it is required. Upload the GST registration '
        + 'certificate PDF issued by the GST portal, and wait for "✓ Uploaded" to appear before submitting.';
    }
    if (!hasFile('cancelled_cheque', 'cancelled_cheque_file_url')) {
      errors.cancelled_cheque =
        'Cancelled Cheque has not been uploaded yet, and it is required. Upload a PDF of a cancelled cheque '
        + 'for the bank account above, and wait for "✓ Uploaded" to appear before submitting.';
    }

    setValidationErrors(errors);
    return errors;
  };

  // Combined error message shown underneath a field. The server's message wins
  // when present, because it explains the rule in more detail than the local
  // pre-check does.
  const getFieldError = (fieldName) => {
    const apiError = apiErrors[fieldName];
    if (apiError) {
      return Array.isArray(apiError) ? apiError[0] : apiError;
    }
    return validationErrors[fieldName] || null;
  };

  // Move focus to the first field the server complained about, so the person
  // lands on the problem instead of hunting for it.
  const focusField = (fieldName) => {
    if (!fieldName || !formRef.current) return;
    // The name comes from the API response, so it is escaped before going into
    // a selector — quotes or brackets in it would otherwise break the query or
    // match an element we did not intend.
    const escaped =
      typeof window !== 'undefined' && window.CSS && typeof window.CSS.escape === 'function'
        ? window.CSS.escape(fieldName)
        : String(fieldName).replace(/["\\]/g, '\\$&');
    const el = formRef.current.querySelector(`[name="${escaped}"]`);
    if (!el) return;
    el.scrollIntoView({ behavior: 'smooth', block: 'center' });
    if (typeof el.focus === 'function') {
      // Focus after the scroll settles so the browser doesn't fight it.
      setTimeout(() => el.focus({ preventScroll: true }), 300);
    }
  };

  // True when there is a list of problems to show, so the headline lives in the
  // summary banner rather than in a separate line above it.
  const hasErrorSummary = !!(apiErrorDetail && apiErrorDetail.issues.length > 0);

  // Summary banner listing every problem, grouped by the section it belongs to.
  const renderApiErrorsBanner = () => {
    if (!hasErrorSummary || summaryDismissed) return null;

    const { issues, sections, count } = apiErrorDetail;
    const heading = count === 1
      ? '1 field needs your attention'
      : `${count} fields need your attention`;


    const dismiss = () => {
      setSummaryDismissed(true);
      setError(null);
    };

    return (
      <div className={styles.warningBanner}>
        <div className={styles.warningHeader}>
          <strong>⚠️ {heading}</strong>
          <button
            type="button"
            className={styles.bannerClose}
            onClick={dismiss}
            aria-label="Close the list of problems"
            title="Close — each message stays under its field"
          >
            ×
          </button>
        </div>
        <div className={styles.warningContent}>
          <p>{apiErrorDetail.message}</p>
          {sections.map((section) => (
            <div key={section.key} className={styles.errorSection}>
              {section.label && <h4 className={styles.errorSectionTitle}>{section.label}</h4>}
              <ul className={styles.errorList}>
                {section.issues.map((issue, index) => (
                  <li key={`${section.key}-${issue.field}-${index}`}>
                    {issue.field ? (
                      <button
                        type="button"
                        className={styles.errorFieldLink}
                        onClick={() => focusField(issue.field)}
                      >
                        {issue.label}
                        {typeof issue.index === 'number' ? ` (entry ${issue.index + 1})` : ''}
                      </button>
                    ) : (
                      <strong>{issue.label}</strong>
                    )}
                    <span className={styles.errorMessage}> {issue.message}</span>
                  </li>
                ))}
              </ul>
            </div>
          ))}
          {sections.length === 0 && (
            <ul className={styles.errorList}>
              {issues.map((issue, index) => (
                <li key={index}>{issue.message}</li>
              ))}
            </ul>
          )}
        </div>
      </div>
    );
  };

  // Handle form submit with better error handling and API error parsing
  const handleSubmit = async (e) => {
    e.preventDefault();

    // Clear previous API errors
    setApiErrors({});
    setApiErrorDetail(null);
    // Whatever was closed last time, the results of this submit are shown.
    setSummaryDismissed(false);

    const localErrors = validateForm();
    if (Object.keys(localErrors).length > 0) {
      const fields = Object.keys(localErrors);
      const count = fields.length;
      setError(
        count === 1
          ? 'One field needs your attention before you can submit. See the details below.'
          : `${count} fields need your attention before you can submit. Each one is listed below.`
      );
      // Build the same detail shape the server produces so the banner, the
      // inline messages and the click-to-jump links all behave identically
      // whether the problem was caught here or by the API.
      setApiErrorDetail({
        message:
          count === 1
            ? 'Please correct the field below and submit again.'
            : 'Please correct the fields below and submit again.',
        count,
        issues: fields.map((field) => ({
          field,
          label: FIELD_LABELS[field] || humanizeFieldName(field),
          message: localErrors[field],
          section: '',
          sectionLabel: '',
        })),
        fieldErrors: localErrors,
        sections: [],
      });
      showToast({
        type: 'error',
        message:
          count === 1
            ? `${FIELD_LABELS[fields[0]] || humanizeFieldName(fields[0])}: ${localErrors[fields[0]]}`
            : `${count} fields need your attention. See the list at the top of the form.`,
      });

      // Scroll to top to show the summary, then land on the first problem.
      window.scrollTo({ top: 0, behavior: 'smooth' });
      focusField(fields[0]);
      return;
    }

    setSaving(true);
    setError(null);

    try {
      let response;
      if (isEditMode && onSave) {
        // Use onSave callback if provided (for edit mode with view/edit toggle)
        await onSave(form);
        setSaved(true);
        setError(null);
        setApiErrors({});
        setApiErrorDetail(null);
        showToast({ type: 'success', message: 'Onboarding details updated successfully' });
      } else if (isEditMode) {
        // Fallback to updateOnboardingForm if onSave not provided
        response = await updateOnboardingForm(token, form);
        setSaved(true);
        setError(null);
        setApiErrors({});
        setApiErrorDetail(null);
      } else {
        // Initial onboarding submission
        response = await submitOnboardingForm(token, form);
        setSaved(true);
        setError(null);
        setApiErrors({});
        setApiErrorDetail(null);

        // Show success message and trigger navigation
        setTimeout(() => {
          if (onFormSubmitted) {
            onFormSubmitted();
          }
        }, 2000); // Wait 2 seconds to show success
      }

    } catch (err) {
      console.error('OnboardingForm: Submit failed:', err);

      // `authenticatedFetch` rejects with an Error carrying the parsed body on
      // `err.data`, so that is where the server's explanation lives.
      const parsed = parseValidationErrors(
        err,
        'We could not reach the server. Please check your connection and try again.'
      );

      if (parsed.isValidationError) {
        setApiErrors(parsed.fieldErrors);
        setApiErrorDetail(parsed);
        setError(parsed.message);
        showToast({ type: 'error', message: summarizeValidationErrors(parsed) });

        // Land on the first field the server rejected.
        const firstField = parsed.issues.find((issue) => issue.field);
        if (firstField) focusField(firstField.field);
      } else {
        setApiErrorDetail(null);
        setError(parsed.message);
        showToast({ type: 'error', message: parsed.message });
      }
      // Toast styles (fallback if not in CSS module)
      const style = document.createElement('style');
      style.innerHTML = `
.onboarding-toast {
  position: fixed;
  top: 24px;
  left: 50%;
  transform: translateX(-50%);
  background: #222;
  color: #fff;
  padding: 12px 32px;
  border-radius: 6px;
  font-size: 1rem;
  z-index: 99999;
  opacity: 0;
  pointer-events: none;
  transition: opacity 0.3s, top 0.3s;
}
.onboarding-toast.show {
  opacity: 1;
  top: 48px;
  pointer-events: auto;
}`;
      if (!document.getElementById('onboarding-toast-style')) {
        style.id = 'onboarding-toast-style';
        document.head.appendChild(style);
      }

      // Scroll to top to show error
      window.scrollTo({ top: 0, behavior: 'smooth' });
    } finally {
      setSaving(false);
    }
  };

  // Helper function to render read-only field value
  const renderReadOnlyValue = (value, emptyText = 'Not provided') => {
    if (!value || (typeof value === 'string' && value.trim() === '')) {
      return <span style={{ color: '#999', fontStyle: 'italic' }}>{emptyText}</span>;
    }
    return <span>{value}</span>;
  };

  // Helper function to render file link
  const renderFileLink = (url, label) => {
    if (!url) return renderReadOnlyValue(null, 'No file uploaded');
    return (
      <a 
        href={url} 
        target="_blank" 
        rel="noopener noreferrer"
        className={styles.fileLink}
        style={{ display: 'inline-block', marginTop: '0.5rem' }}
      >
        {label}
      </a>
    );
  };

  // Helper function to get category name from ID
  const getCategoryName = (categoryId) => {
    if (!categoryId) return '';
    const category = categories.find(cat => cat.id === categoryId);
    return category ? category.name : categoryId;
  };

  // Helper function to render checkbox value in read-only mode
  const renderCheckboxValue = (checked) => {
    return <span style={{ color: checked ? '#388e3c' : '#999' }}>{checked ? 'Yes' : 'No'}</span>;
  };

  // Determine if we're in read-only view mode
  const isReadOnly = isEditMode && isViewMode;

  // Render form fields with horizontal layout
  return (
    <div className={styles.container}>
      <form className={styles.form} onSubmit={handleSubmit} ref={formRef}>
        <div className={styles.header}>
          <h2>{isEditMode ? 'Onboarding Details' : 'Brand Onboarding Form'}</h2>
          {isReadOnly && onEdit && (
            <button
              type="button"
              onClick={onEdit}
              className={styles.editButton}
            >
              Edit Details
            </button>
          )}
        </div>


        {error && !hasErrorSummary && (
          <div className={styles.error}>
            <span>{error}</span>
            <button
              type="button"
              className={styles.bannerClose}
              onClick={() => setError(null)}
              aria-label="Dismiss this message"
              title="Dismiss"
            >
              ×
            </button>
          </div>
        )}
        {fileError && <div className={styles.error}>{fileError}</div>}
        {renderApiErrorsBanner()}

        <div className={styles.formContent}>
          <div className={styles.sectionsGrid}>

            {/* Brand & Contact Information */}
            <div className={styles.section}>
              <h3>Brand & Contact Information</h3>
              <div className={styles.row}>
                <div className={styles.field}>
                  <label>Brand Name *</label>
                  {isReadOnly ? (
                    <div className={styles.readOnlyValue}>{renderReadOnlyValue(form.brand_name)}</div>
                  ) : (
                    <>
                      <input
                        type="text"
                        name="brand_name"
                        value={form.brand_name || ''}
                        onChange={handleChange}
                        maxLength={100}
                        required
                      />
                      {getFieldError("brand_name") && <span className={styles.fieldError}>{getFieldError("brand_name")}</span>}
                    </>
                  )}
                </div>
                <div className={styles.field}>
                  <label>Brand Point of Contact (POC) Name *</label>
                  {isReadOnly ? (
                    <div className={styles.readOnlyValue}>{renderReadOnlyValue(form.brand_poc_name)}</div>
                  ) : (
                    <>
                      <input
                        type="text"
                        name="brand_poc_name"
                        value={form.brand_poc_name || ''}
                        onChange={handleChange}
                        maxLength={100}
                        required
                      />
                      {getFieldError("brand_poc_name") && <span className={styles.fieldError}>{getFieldError("brand_poc_name")}</span>}
                    </>
                  )}
                </div>
                <div className={styles.field}>
                  <label>Brand POC Number *</label>
                  {isReadOnly ? (
                    <div className={styles.readOnlyValue}>{renderReadOnlyValue(form.brand_poc_number)}</div>
                  ) : (
                    <>
                      <input
                        type="tel"
                        name="brand_poc_number"
                        value={form.brand_poc_number || ''}
                        onChange={handleChange}
                        maxLength={20}
                        required
                      />
                      {getFieldError("brand_poc_number") && <span className={styles.fieldError}>{getFieldError("brand_poc_number")}</span>}
                    </>
                  )}
                </div>
                <div className={styles.field}>
                  <label>Email Address *</label>
                  {isReadOnly ? (
                    <div className={styles.readOnlyValue}>{renderReadOnlyValue(form.email_address)}</div>
                  ) : (
                    <>
                      <input
                        type="email"
                        name="email_address"
                        value={form.email_address || ''}
                        onChange={handleChange}
                        maxLength={100}
                        required
                      />
                      {getFieldError("email_address") && <span className={styles.fieldError}>{getFieldError("email_address")}</span>}
                    </>
                  )}
                </div>
                <div className={styles.field}>
                  <label>General Contact Number *</label>
                  {isReadOnly ? (
                    <div className={styles.readOnlyValue}>{renderReadOnlyValue(form.general_contact_number)}</div>
                  ) : (
                    <>
                      <input
                        type="tel"
                        name="general_contact_number"
                        value={form.general_contact_number || ''}
                        onChange={handleChange}
                        maxLength={20}
                      />
                      {getFieldError("general_contact_number") && <span className={styles.fieldError}>{getFieldError("general_contact_number")}</span>}
                    </>
                  )}
                </div>
                <div className={styles.field}>
                  <label>Brand Logo</label>
                  {isReadOnly ? (
                    <div className={styles.readOnlyValue}>
                      {renderFileLink(form.brand_logo_url, 'View Brand Logo')}
                    </div>
                  ) : (
                    <>
                      {form.brand_logo_url && (
                        <div className={styles.fileLinkContainer}>
                          <a 
                            href={form.brand_logo_url} 
                            target="_blank" 
                            rel="noopener noreferrer"
                            className={styles.fileLink}
                          >
                            View Current Brand Logo
                          </a>
                        </div>
                      )}
                      <input
                        type="file"
                        name="brand_logo"
                        accept="image/*"
                        onChange={e => handleFileChange(e, 'brand_logo')}
                      />
                      {fileUploading.brand_logo && <span className={styles.uploading}>Uploading...</span>}
                      {form.brand_logo_url && !fileUploading.brand_logo && (
                        <span className={styles.uploaded}>✓ Uploaded</span>
                      )}
                    </>
                  )}
                </div>
              </div>
            </div>

            {/* Company Information */}
            <div className={styles.section}>
              <h3>Company Information</h3>
              <div className={styles.row}>
                <div className={styles.field}>
                  <label>Company Name *</label>
                  {isReadOnly ? (
                    <div className={styles.readOnlyValue}>{renderReadOnlyValue(form.company_name)}</div>
                  ) : (
                    <>
                      <input
                        type="text"
                        name="company_name"
                        value={form.company_name || ''}
                        onChange={handleChange}
                        maxLength={200}
                        required
                      />
                      {getFieldError("company_name") && <span className={styles.fieldError}>{getFieldError("company_name")}</span>}
                    </>
                  )}
                </div>
                <div className={styles.field}>
                  <label>Company Address *</label>
                  {isReadOnly ? (
                    <div className={styles.readOnlyValue}>{renderReadOnlyValue(form.company_address)}</div>
                  ) : (
                    <>
                      <textarea
                        name="company_address"
                        value={form.company_address || ''}
                        onChange={handleChange}
                        rows={2}
                        maxLength={300}
                        required
                      />
                      {getFieldError("company_address") && <span className={styles.fieldError}>{getFieldError("company_address")}</span>}
                    </>
                  )}
                </div>
                <div className={styles.field}>
                  <label>Billing Address *</label>
                  {isReadOnly ? (
                    <div className={styles.readOnlyValue}>{renderReadOnlyValue(form.billing_address)}</div>
                  ) : (
                    <>
                      <textarea
                        name="billing_address"
                        value={form.billing_address || ''}
                        onChange={handleChange}
                        rows={2}
                        maxLength={300}
                        required
                      />
                      {getFieldError("billing_address") && <span className={styles.fieldError}>{getFieldError("billing_address")}</span>}
                    </>
                  )}
                </div>
                <div className={styles.field}>
                  <label>Order Processing Timeline *</label>
                  {isReadOnly ? (
                    <div className={styles.readOnlyValue}>{renderReadOnlyValue(form.general_processing_timeline)}</div>
                  ) : (
                    <>
                      <input
                        type="text"
                        name="general_processing_timeline"
                        value={form.general_processing_timeline || ''}
                        onChange={handleChange}
                        maxLength={100}
                        placeholder="e.g., 3-5 business days"
                        required
                      />
                      {getFieldError("general_processing_timeline") && <span className={styles.fieldError}>{getFieldError("general_processing_timeline")}</span>}
                    </>
                  )}
                </div>
                <div className={styles.field}>
                  <label>Country of Origin *</label>
                  {isReadOnly ? (
                    <div className={styles.readOnlyValue}>{renderReadOnlyValue(form.country_of_origin)}</div>
                  ) : (
                    <>
                      <input
                        type="text"
                        name="country_of_origin"
                        value={form.country_of_origin || ''}
                        onChange={handleChange}
                        maxLength={100}
                        required
                      />
                      {getFieldError("country_of_origin") && <span className={styles.fieldError}>{getFieldError("country_of_origin")}</span>}
                    </>
                  )}
                </div>
              </div>
            </div>

            {/* Warehouse Information */}
            <div className={styles.section}>
              <h3>Warehouse Information</h3>
              <div className={styles.row}>
                <div className={styles.field}>
                  <label>Warehouse POC Name *</label>
                  {isReadOnly ? (
                    <div className={styles.readOnlyValue}>{renderReadOnlyValue(form.warehouse_poc_name)}</div>
                  ) : (
                    <>
                      <input
                        type="text"
                        name="warehouse_poc_name"
                        value={form.warehouse_poc_name || ''}
                        onChange={handleChange}
                        maxLength={100}
                        required
                      />
                      {getFieldError("warehouse_poc_name") && <span className={styles.fieldError}>{getFieldError("warehouse_poc_name")}</span>}
                    </>
                  )}
                </div>
                <div className={styles.field}>
                  <label>Warehouse POC Number *</label>
                  {isReadOnly ? (
                    <div className={styles.readOnlyValue}>{renderReadOnlyValue(form.warehouse_poc_number)}</div>
                  ) : (
                    <>
                      <input
                        type="tel"
                        name="warehouse_poc_number"
                        value={form.warehouse_poc_number || ''}
                        onChange={handleChange}
                        maxLength={20}
                        required
                      />
                      {getFieldError("warehouse_poc_number") && <span className={styles.fieldError}>{getFieldError("warehouse_poc_number")}</span>}
                    </>
                  )}
                </div>
                <div className={styles.field}>
                  <label>Warehouse POC Address *</label>
                  {isReadOnly ? (
                    <div className={styles.readOnlyValue}>{renderReadOnlyValue(form.warehouse_poc_address)}</div>
                  ) : (
                    <>
                      <textarea
                        name="warehouse_poc_address"
                        value={form.warehouse_poc_address || ''}
                        onChange={handleChange}
                        rows={2}
                        required
                        maxLength={300}
                      />
                      {getFieldError("warehouse_poc_address") && <span className={styles.fieldError}>{getFieldError("warehouse_poc_address")}</span>}
                    </>
                  )}
                </div>
              </div>
            </div>

            {/* Operations Information */}
            <div className={styles.section}>
              <h3>Operations Information</h3>
              <div className={styles.row}>
                <div className={styles.field}>
                  <label>Operations POC Name *</label>
                  {isReadOnly ? (
                    <div className={styles.readOnlyValue}>{renderReadOnlyValue(form.operations_poc_name)}</div>
                  ) : (
                    <>
                      <input
                        type="text"
                        name="operations_poc_name"
                        value={form.operations_poc_name || ''}
                        onChange={handleChange}
                        maxLength={100}
                        required
                      />
                      {getFieldError("operations_poc_name") && <span className={styles.fieldError}>{getFieldError("operations_poc_name")}</span>}
                    </>
                  )}
                </div>
                <div className={styles.field}>
                  <label>Operations POC Number *</label>
                  {isReadOnly ? (
                    <div className={styles.readOnlyValue}>{renderReadOnlyValue(form.operations_poc_number)}</div>
                  ) : (
                    <>
                      <input
                        type="tel"
                        name="operations_poc_number"
                        value={form.operations_poc_number || ''}
                        onChange={handleChange}
                        maxLength={20}
                        required
                      />
                      {getFieldError("operations_poc_number") && <span className={styles.fieldError}>{getFieldError("operations_poc_number")}</span>}
                    </>
                  )}
                </div>
                <div className={styles.field}>
                  <label>Operations POC Email *</label>
                  {isReadOnly ? (
                    <div className={styles.readOnlyValue}>{renderReadOnlyValue(form.operations_poc_email)}</div>
                  ) : (
                    <>
                      <input
                        type="email"
                        name="operations_poc_email"
                        value={form.operations_poc_email || ''}
                        onChange={handleChange}
                        required
                      />
                      {getFieldError("operations_poc_email") && <span className={styles.fieldError}>{getFieldError("operations_poc_email")}</span>}
                    </>
                  )}
                </div>
              </div>
            </div>

            {/* Business Policies */}
            <div className={styles.section}>
              <h3>Business Policies</h3>
              <div className={styles.policiesGrid}>
                <div className={styles.policiesLeft}>
                  {/* Exchange Policy */}
                  <div className={`${styles.field} ${styles.policyToggle}`}>
                    <label>
                      {isReadOnly ? (
                        <span>Is exchange allowed? {renderCheckboxValue(form.exchanges)}</span>
                      ) : (
                        <>
                          <input
                            type="checkbox"
                            name="exchanges"
                            checked={form.exchanges || false}
                            onChange={handleChange}
                          />
                          Is exchange allowed?
                        </>
                      )}
                    </label>
                  </div>

                  {/* Return Policy */}
                  <div className={`${styles.field} ${styles.policyToggle}`}>
                    <label>
                      {isReadOnly ? (
                        <span>Is return allowed? {renderCheckboxValue(form.returns)}</span>
                      ) : (
                        <>
                          <input
                            type="checkbox"
                            name="returns"
                            checked={form.returns || false}
                            onChange={handleChange}
                          />
                          Is return allowed?
                        </>
                      )}
                    </label>
                  </div>

                  {/* Made-to-order products? */}
                  <div className={`${styles.field} ${styles.policyToggle}`}>
                    <label>
                      {isReadOnly ? (
                        <span>Made-to-order products? {renderCheckboxValue(form.made_to_order)}</span>
                      ) : (
                        <>
                          <input
                            type="checkbox"
                            name="made_to_order"
                            checked={form.made_to_order || false}
                            onChange={handleChange}
                          />
                          Made-to-order products?
                        </>
                      )}
                    </label>
                  </div>
                </div>

                <div className={styles.policiesRight}>
                  {form.exchanges && (
                    <div className={styles.policyCard}>
                      <label>Exchange Policy Details:</label>
                      <div className={styles.checkboxGroup}>
                        {isReadOnly ? (
                          <>
                            {form.exchange_damaged && <div>✓ Exchange allowed only if the product is damaged or defective</div>}
                            {form.exchange_wrong_item && <div>✓ Exchange allowed only if the wrong item is received</div>}
                            {form.exchange_size_issues && <div>✓ Exchange allowed in case of size issues (if size variants exist)</div>}
                            {form.exchange_7_days && <div>✓ Exchange allowed within 7 days of delivery</div>}
                            {form.exchange_15_days && <div>✓ Exchange allowed within 15 days of delivery</div>}
                            {form.exchange_unused && <div>✓ Exchange allowed only if the product is unused and in original packaging</div>}
                            {form.exchange_one_per_order && <div>✓ Only one exchange allowed per order</div>}
                            {form.exchange_same_product && <div>✓ Exchange only for same product (e.g., size/color change)</div>}
                            {form.exchange_selected_products && <div>✓ Exchange applicable only for selected products</div>}
                          </>
                        ) : (
                          <>
                            <label style={{ display: 'block' }}><input type="checkbox" name="exchange_damaged" checked={form.exchange_damaged || false} onChange={handleChange} /> Exchange allowed only if the product is damaged or defective</label>
                            <label style={{ display: 'block' }}><input type="checkbox" name="exchange_wrong_item" checked={form.exchange_wrong_item || false} onChange={handleChange} /> Exchange allowed only if the wrong item is received</label>
                            <label style={{ display: 'block' }}><input type="checkbox" name="exchange_size_issues" checked={form.exchange_size_issues || false} onChange={handleChange} /> Exchange allowed in case of size issues (if size variants exist)</label>
                            <label style={{ display: 'block' }}><input type="checkbox" name="exchange_7_days" checked={form.exchange_7_days || false} onChange={handleChange} /> Exchange allowed within 7 days of delivery</label>
                            <label style={{ display: 'block' }}><input type="checkbox" name="exchange_15_days" checked={form.exchange_15_days || false} onChange={handleChange} /> Exchange allowed within 15 days of delivery</label>
                            <label style={{ display: 'block' }}><input type="checkbox" name="exchange_unused" checked={form.exchange_unused || false} onChange={handleChange} /> Exchange allowed only if the product is unused and in original packaging</label>
                            <label style={{ display: 'block' }}><input type="checkbox" name="exchange_one_per_order" checked={form.exchange_one_per_order || false} onChange={handleChange} /> Only one exchange allowed per order</label>
                            <label style={{ display: 'block' }}><input type="checkbox" name="exchange_same_product" checked={form.exchange_same_product || false} onChange={handleChange} /> Exchange only for same product (e.g., size/color change)</label>
                            <label style={{ display: 'block' }}><input type="checkbox" name="exchange_selected_products" checked={form.exchange_selected_products || false} onChange={handleChange} /> Exchange applicable only for selected products</label>
                          </>
                        )}
                      </div>
                    </div>
                  )}

                  {form.returns && (
                    <div className={styles.policyCard}>
                      <label>Return Policy Details:</label>
                      <div className={styles.checkboxGroup}>
                        {isReadOnly ? (
                          <>
                            {form.return_damaged && <div>✓ Return allowed only if the product is damaged or defective</div>}
                            {form.return_wrong_item && <div>✓ Return allowed only if the wrong item is received</div>}
                            {form.return_7_days && <div>✓ Return allowed within 7 days of delivery</div>}
                            {form.return_15_days && <div>✓ Return allowed within 15 days of delivery</div>}
                            {form.return_unused_unopened && <div>✓ Return allowed for unused and unopened products only</div>}
                            {form.return_original_packaging && <div>✓ Return accepted only if product is in original packaging with all tags intact</div>}
                            {form.return_hygiene_products && <div>✓ Return not allowed for hygiene-based products (e.g., skincare, undergarments)</div>}
                            {form.return_selected_products && <div>✓ Return applicable only for selected products</div>}
                          </>
                        ) : (
                          <>
                            <label style={{ display: 'block' }}><input type="checkbox" name="return_damaged" checked={form.return_damaged || false} onChange={handleChange} /> Return allowed only if the product is damaged or defective</label>
                            <label style={{ display: 'block' }}><input type="checkbox" name="return_wrong_item" checked={form.return_wrong_item || false} onChange={handleChange} /> Return allowed only if the wrong item is received</label>
                            <label style={{ display: 'block' }}><input type="checkbox" name="return_7_days" checked={form.return_7_days || false} onChange={handleChange} /> Return allowed within 7 days of delivery</label>
                            <label style={{ display: 'block' }}><input type="checkbox" name="return_15_days" checked={form.return_15_days || false} onChange={handleChange} /> Return allowed within 15 days of delivery</label>
                            <label style={{ display: 'block' }}><input type="checkbox" name="return_unused_unopened" checked={form.return_unused_unopened || false} onChange={handleChange} /> Return allowed for unused and unopened products only</label>
                            <label style={{ display: 'block' }}><input type="checkbox" name="return_original_packaging" checked={form.return_original_packaging || false} onChange={handleChange} /> Return accepted only if product is in original packaging with all tags intact</label>
                            <label style={{ display: 'block' }}><input type="checkbox" name="return_hygiene_products" checked={form.return_hygiene_products || false} onChange={handleChange} /> Return not allowed for hygiene-based products (e.g., skincare, undergarments)</label>
                            <label style={{ display: 'block' }}><input type="checkbox" name="return_selected_products" checked={form.return_selected_products || false} onChange={handleChange} /> Return applicable only for selected products</label>
                          </>
                        )}
                      </div>
                    </div>
                  )}

                  {form.made_to_order && (
                    <div className={styles.policyCard}>
                      <div className={styles.field}>
                        <label>Dispatch Timelines *</label>
                        {isReadOnly ? (
                          <div className={styles.readOnlyValue}>{renderReadOnlyValue(form.dispatch_timelines)}</div>
                        ) : (
                          <>
                            <textarea
                              name="dispatch_timelines"
                              value={form.dispatch_timelines || ''}
                              onChange={handleChange}
                              rows={2}
                              placeholder="Dispatch timelines for made-to-order..."
                              required
                            />
                            {getFieldError("dispatch_timelines") && <span className={styles.fieldError}>{getFieldError("dispatch_timelines")}</span>}
                          </>
                        )}
                      </div>
                    </div>
                  )}
                </div>
              </div>
            </div>

            {/* Legal and Financial Information */}
            <div className={styles.section}>
              <h3>Legal & Financial</h3>
              <div className={styles.row}>
                <div className={styles.field}>
                  <label>GSTIN *</label>
                  {isReadOnly ? (
                    <div className={styles.readOnlyValue}>{renderReadOnlyValue(form.gstin)}</div>
                  ) : (
                    <>
                      <input
                        type="text"
                        name="gstin"
                        value={form.gstin || ''}
                        onChange={handleChange}
                        maxLength={15}
                        placeholder="15-character GSTIN"
                        required
                        disabled={form.gstin_locked}
                      />
                      {getFieldError("gstin") && <span className={styles.fieldError}>{getFieldError("gstin")}</span>}
                      {form.gstin_locked && <span className={styles.info}>GSTIN is locked after submission</span>}
                    </>
                  )}
                </div>

                {/* The HSN Code field has been removed as per the requirements. */}

                {/* NEW: GSTIN Certificate Upload */}
                <div className={styles.field}>
                  <label>GSTIN Certificate *</label>
                  {isReadOnly ? (
                    <div className={styles.readOnlyValue}>
                      {renderFileLink(form.gstin_certificate_file_url, 'View GSTIN Certificate')}
                    </div>
                  ) : (
                    <>
                      {form.gstin_certificate_file_url && (
                        <div className={styles.fileLinkContainer}>
                          <a 
                            href={form.gstin_certificate_file_url} 
                            target="_blank" 
                            rel="noopener noreferrer"
                            className={styles.fileLink}
                          >
                            View Current GSTIN Certificate
                          </a>
                        </div>
                      )}
                      <input
                        type="file"
                        name="gstin_certificate"
                        accept=".pdf"
                        onChange={e => handleFileChange(e, 'gstin_certificate')}
                      />
                      {fileUploading.gstin_certificate && (
                        <span className={styles.uploading}>Uploading...</span>
                      )}
                      {(form.gstin_certificate || form.gstin_certificate_file_url) &&
                        !fileUploading.gstin_certificate && (
                          <span className={styles.uploaded}>✓ Uploaded</span>
                        )}
                      {getFieldError("gstin_certificate") && <span className={styles.fieldError}>{getFieldError("gstin_certificate")}</span>}
                    </>
                  )}
                </div>

                {/* NEW: Cancelled Cheque Upload */}
                <div className={styles.field}>
                  <label>Cancelled Cheque *</label>
                  {isReadOnly ? (
                    <div className={styles.readOnlyValue}>
                      {renderFileLink(form.cancelled_cheque_file_url, 'View Cancelled Cheque')}
                    </div>
                  ) : (
                    <>
                      {form.cancelled_cheque_file_url && (
                        <div className={styles.fileLinkContainer}>
                          <a 
                            href={form.cancelled_cheque_file_url} 
                            target="_blank" 
                            rel="noopener noreferrer"
                            className={styles.fileLink}
                          >
                            View Current Cancelled Cheque
                          </a>
                        </div>
                      )}
                      <input
                        type="file"
                        name="cancelled_cheque"
                        accept=".pdf"
                        onChange={e => handleFileChange(e, 'cancelled_cheque')}
                      />
                      {fileUploading.cancelled_cheque && (
                        <span className={styles.uploading}>Uploading...</span>
                      )}
                      {(form.cancelled_cheque || form.cancelled_cheque_file_url) &&
                        !fileUploading.cancelled_cheque && (
                          <span className={styles.uploaded}>✓ Uploaded</span>
                        )}
                      {getFieldError("cancelled_cheque") && <span className={styles.fieldError}>{getFieldError("cancelled_cheque")}</span>}
                    </>
                  )}
                </div>
              </div>
            </div>

            {/* Banking Information */}
            <div className={styles.section}>
              <h3>Banking Information</h3>
              <div className={styles.row}>
                <div className={styles.field}>
                  <label>Bank Name *</label>
                  {isReadOnly ? (
                    <div className={styles.readOnlyValue}>{renderReadOnlyValue(form.bank_name)}</div>
                  ) : (
                    <>
                      <input
                        type="text"
                        name="bank_name"
                        value={form.bank_name || ''}
                        onChange={handleChange}
                        maxLength={100}
                        required
                      />
                      {getFieldError("bank_name") && <span className={styles.fieldError}>{getFieldError("bank_name")}</span>}
                    </>
                  )}
                </div>
                <div className={styles.field}>
                  <label>Account Number *</label>
                  {isReadOnly ? (
                    <div className={styles.readOnlyValue}>{renderReadOnlyValue(form.bank_account_no)}</div>
                  ) : (
                    <>
                      <input
                        type="text"
                        name="bank_account_no"
                        value={form.bank_account_no || ''}
                        onChange={handleChange}
                        maxLength={20}
                        required
                      />
                      {getFieldError("bank_account_no") && <span className={styles.fieldError}>{getFieldError("bank_account_no")}</span>}
                    </>
                  )}
                </div>
                <div className={styles.field}>
                  <label>Account Type *</label>
                  {isReadOnly ? (
                    <div className={styles.readOnlyValue}>{renderReadOnlyValue(form.bank_account_type ? form.bank_account_type.charAt(0).toUpperCase() + form.bank_account_type.slice(1) : '')}</div>
                  ) : (
                    <>
                      <select
                        name="bank_account_type"
                        value={form.bank_account_type || ''}
                        onChange={handleChange}
                        required
                      >
                        <option value="">Select Type</option>
                        <option value="savings">Savings</option>
                        <option value="current">Current</option>
                        <option value="cc">Credit Card</option>
                        <option value="od">Overdraft</option>
                      </select>
                      {getFieldError("bank_account_type") && <span className={styles.fieldError}>{getFieldError("bank_account_type")}</span>}
                    </>
                  )}
                </div>
                <div className={styles.field}>
                  <label>IFSC Code *</label>
                  {isReadOnly ? (
                    <div className={styles.readOnlyValue}>{renderReadOnlyValue(form.bank_ifsc)}</div>
                  ) : (
                    <>
                      <input
                        type="text"
                        name="bank_ifsc"
                        value={form.bank_ifsc || ''}
                        onChange={handleChange}
                        maxLength={11}
                        placeholder="11-character IFSC"
                        required
                      />
                      {getFieldError("bank_ifsc") && <span className={styles.fieldError}>{getFieldError("bank_ifsc")}</span>}
                    </>
                  )}
                </div>
                <div className={styles.field}>
                  <label>Account Holder Name *</label>
                  {isReadOnly ? (
                    <div className={styles.readOnlyValue}>{renderReadOnlyValue(form.account_holder_name)}</div>
                  ) : (
                    <>
                      <input
                        type="text"
                        name="account_holder_name"
                        value={form.account_holder_name || ''}
                        onChange={handleChange}
                        maxLength={100}
                        required
                      />
                      {getFieldError("account_holder_name") && <span className={styles.fieldError}>{getFieldError("account_holder_name")}</span>}
                    </>
                  )}
                </div>
                <div className={styles.field}>
                  <label>Bank Branch</label>
                  {isReadOnly ? (
                    <div className={styles.readOnlyValue}>{renderReadOnlyValue(form.bank_branch)}</div>
                  ) : (
                    <input
                      type="text"
                      name="bank_branch"
                      value={form.bank_branch || ''}
                      onChange={handleChange}
                      maxLength={100}
                    />
                  )}
                </div>
              </div>
            </div>

            {/* Manufacturing Information */}
            <div className={styles.section}>
              <h3>Manufacturing</h3>
              <div className={styles.row}>
                <div className={styles.field}>
                  <label>Manufacturing Country *</label>
                  {isReadOnly ? (
                    <div className={styles.readOnlyValue}>{renderReadOnlyValue(form.manufacturing_country)}</div>
                  ) : (
                    <>
                      <input
                        type="text"
                        name="manufacturing_country"
                        value={form.manufacturing_country || ''}
                        onChange={handleChange}
                        maxLength={100}
                        required
                      />
                      {getFieldError("manufacturing_country") && <span className={styles.fieldError}>{getFieldError("manufacturing_country")}</span>}
                    </>
                  )}
                </div>

                {/* NEW: Manufacturer and Packer details */}
                <div className={styles.field}>
                  <label>Manufacturer Name *</label>
                  {isReadOnly ? (
                    <div className={styles.readOnlyValue}>{renderReadOnlyValue(form.manufacturer_name)}</div>
                  ) : (
                    <>
                      <input
                        type="text"
                        name="manufacturer_name"
                        value={form.manufacturer_name || ''}
                        onChange={handleChange}
                        maxLength={200}
                        required
                      />
                      {getFieldError("manufacturer_name") && <span className={styles.fieldError}>{getFieldError("manufacturer_name")}</span>}
                    </>
                  )}
                </div>
                <div className={styles.field}>
                  <label>Manufacturer Address *</label>
                  {isReadOnly ? (
                    <div className={styles.readOnlyValue}>{renderReadOnlyValue(form.manufacturer_address)}</div>
                  ) : (
                    <>
                      <textarea
                        name="manufacturer_address"
                        value={form.manufacturer_address || ''}
                        onChange={handleChange}
                        rows={2}
                        required
                      />
                      {getFieldError("manufacturer_address") && <span className={styles.fieldError}>{getFieldError("manufacturer_address")}</span>}
                    </>
                  )}
                </div>

                <div className={styles.field}>
                  <label>
                    {isReadOnly ? (
                      <span>Is the packer same as the manufacturer? {renderCheckboxValue(form.manufacturing_and_packer_same)}</span>
                    ) : (
                      <>
                        <input
                          type="checkbox"
                          name="manufacturing_and_packer_same"
                          checked={form.manufacturing_and_packer_same || false}
                          onChange={handleChange}
                        />
                        Is the packer same as the manufacturer?
                      </>
                    )}
                  </label>
                </div>

                {/* Conditionally rendered fields for when packer is NOT the same */}
                {!form.manufacturing_and_packer_same && (
                  <>
                    <div className={styles.field}>
                      <label>Packer Name *</label>
                      {isReadOnly ? (
                        <div className={styles.readOnlyValue}>{renderReadOnlyValue(form.packer_name)}</div>
                      ) : (
                        <>
                          <input
                            type="text"
                            name="packer_name"
                            value={form.packer_name || ''}
                            onChange={handleChange}
                            maxLength={200}
                            required
                          />
                          {getFieldError("packer_name") && <span className={styles.fieldError}>{getFieldError("packer_name")}</span>}
                        </>
                      )}
                    </div>
                    <div className={styles.field}>
                      <label>Packer Address *</label>
                      {isReadOnly ? (
                        <div className={styles.readOnlyValue}>{renderReadOnlyValue(form.packer_address)}</div>
                      ) : (
                        <>
                          <textarea
                            name="packer_address"
                            value={form.packer_address || ''}
                            onChange={handleChange}
                            rows={2}
                            required
                          />
                          {getFieldError("packer_address") && <span className={styles.fieldError}>{getFieldError("packer_address")}</span>}
                        </>
                      )}
                    </div>
                  </>
                )}

                {/* The shipper fields are always shown */}
                <div className={styles.field}>
                  <label>Shipper Name</label>
                  {isReadOnly ? (
                    <div className={styles.readOnlyValue}>{renderReadOnlyValue(form.shipper_name)}</div>
                  ) : (
                    <input
                      type="text"
                      name="shipper_name"
                      value={form.shipper_name || ''}
                      onChange={handleChange}
                      maxLength={200}
                    />
                  )}
                </div>
                <div className={styles.field}>
                  <label>Shipper Address</label>
                  {isReadOnly ? (
                    <div className={styles.readOnlyValue}>{renderReadOnlyValue(form.shipper_address)}</div>
                  ) : (
                    <textarea
                      name="shipper_address"
                      value={form.shipper_address || ''}
                      onChange={handleChange}
                      rows={2}
                    />
                  )}
                </div>

                {/* Existing packaging materials field */}
                <div className={styles.field}>
                  <label>Packaging Materials</label>
                  {isReadOnly ? (
                    <div className={styles.readOnlyValue}>{renderReadOnlyValue(form.packaging_materials_used)}</div>
                  ) : (
                    <textarea
                      name="packaging_materials_used"
                      value={form.packaging_materials_used || ''}
                      onChange={handleChange}
                      rows={2}
                      placeholder="Describe packaging materials..."
                    />
                  )}
                </div>
                <div className={styles.uploadField}>
                  <label>Manufacturer Excel</label>
                  {isReadOnly ? (
                    <div className={styles.readOnlyValue}>
                      {renderFileLink(form.manufacturer_excel_file_url, 'View Manufacturer Excel')}
                    </div>
                  ) : (
                    <>
                      {form.manufacturer_excel_file_url && (
                        <div className={styles.fileLinkContainer}>
                          <a 
                            href={form.manufacturer_excel_file_url} 
                            target="_blank" 
                            rel="noopener noreferrer"
                            className={styles.fileLink}
                          >
                            View Current Manufacturer Excel
                          </a>
                        </div>
                      )}
                      <input
                        type="file"
                        name="manufacturer_excel"
                        accept="application/pdf"
                        onChange={e => handleFileChange(e, 'manufacturer_excel')}
                      />
                      {fileUploading.manufacturer_excel && (
                        <span className={styles.uploading}>Uploading...</span>
                      )}
                      {(form.manufacturer_excel || form.manufacturer_excel_file_url) &&
                        !fileUploading.manufacturer_excel && (
                          <span className={styles.uploaded}>✓ Uploaded</span>
                        )}
                    </>
                  )}
                </div>

                {/* NEW: Import Information (conditionally rendered and disabled) */}
                {form.manufacturing_country && form.manufacturing_country.toLowerCase() !== 'india' && (
                  <>
                    <div className={styles.field}>
                      <label>Importer of Record (IOR) *</label>
                      {isReadOnly ? (
                        <div className={styles.readOnlyValue}>{renderReadOnlyValue(form.importer_of_record)}</div>
                      ) : (
                        <>
                          <input
                            type="text"
                            name="importer_of_record"
                            value={form.importer_of_record || ''}
                            onChange={handleChange}
                            maxLength={200}
                            required
                          />
                          {getFieldError("importer_of_record") && <span className={styles.fieldError}>{getFieldError("importer_of_record")}</span>}
                        </>
                      )}
                    </div>
                    <div className={styles.field}>
                      <label>Import License Number *</label>
                      {isReadOnly ? (
                        <div className={styles.readOnlyValue}>{renderReadOnlyValue(form.import_license_number)}</div>
                      ) : (
                        <>
                          <input
                            type="text"
                            name="import_license_number"
                            value={form.import_license_number || ''}
                            onChange={handleChange}
                            maxLength={50}
                            required
                          />
                          {getFieldError("import_license_number") && <span className={styles.fieldError}>{getFieldError("import_license_number")}</span>}
                        </>
                      )}
                    </div>
                  </>
                )}

              </div>
            </div>

            {/* Product Categorization */}
            <div className={styles.section}>
              <h3>Product Categorization</h3>
              <div className={styles.row}>
                <div className={styles.field}>
                  <label>Suspire Category *</label>
                  {isReadOnly ? (
                    <div className={styles.readOnlyValue}>{renderReadOnlyValue(getCategoryName(form.suspire_category))}</div>
                  ) : (
                    <>
                      <select
                        name="suspire_category"
                        value={form.suspire_category || ''}
                        onChange={handleChange}
                        required
                      >
                        <option value="">Select Category</option>
                        {categories.map(cat => (
                          <option key={cat.id} value={cat.id}>{cat.name}</option>
                        ))}
                      </select>
                      {getFieldError("suspire_category") && <span className={styles.fieldError}>{getFieldError("suspire_category")}</span>}
                    </>
                  )}
                </div>
                {/* Sizing Chart (conditionally rendered) */}
                {(form.suspire_category == 'Apparel & Fashion' || form.suspire_category == 'Footwear') && (
                  <div className={styles.field}>
                    <label>Sizing Chart (Google Drive Link)</label>
                    {isReadOnly ? (
                      <div className={styles.readOnlyValue}>
                        {form.sizing_chart_link ? (
                          <a href={form.sizing_chart_link} target="_blank" rel="noopener noreferrer" className={styles.fileLink}>
                            {form.sizing_chart_link}
                          </a>
                        ) : (
                          renderReadOnlyValue(null, 'Not provided')
                        )}
                      </div>
                    ) : (
                      <input
                        type="url"
                        name="sizing_chart_link"
                        value={form.sizing_chart_link || ''}
                        onChange={handleChange}
                        placeholder="https://drive.google.com/..."
                      />
                    )}
                  </div>
                )}

                {/* NEW: Signed Agreement Upload */}
                <div className={styles.uploadField}>
                  <label>Signed Agreement</label>
                  {isReadOnly ? (
                    <div className={styles.readOnlyValue}>
                      {renderFileLink(form.signed_agreement_file_url, 'View Signed Agreement')}
                    </div>
                  ) : (
                    <>
                      {form.signed_agreement_file_url && (
                        <div className={styles.fileLinkContainer}>
                          <a 
                            href={form.signed_agreement_file_url} 
                            target="_blank" 
                            rel="noopener noreferrer"
                            className={styles.fileLink}
                          >
                            View Current Signed Agreement
                          </a>
                        </div>
                      )}
                      <input
                        type="file"
                        name="signed_agreement"
                        accept="application/pdf"
                        onChange={e => handleFileChange(e, 'signed_agreement')}
                      />
                      {fileUploading.signed_agreement && (
                        <span className={styles.uploading}>Uploading...</span>
                      )}
                      {(form.signed_agreement || form.signed_agreement_file_url) &&
                        !fileUploading.signed_agreement && (
                          <span className={styles.uploaded}>✓ Uploaded</span>
                        )}
                    </>
                  )}
                </div>

                {/* NEW: Certifications Upload */}
                <div className={styles.uploadField}>
                  <label>Certifications</label>
                  {isReadOnly ? (
                    <div className={styles.readOnlyValue}>
                      {renderFileLink(form.certifications_url, 'View Certifications')}
                    </div>
                  ) : (
                    <>
                      {form.certifications_url && (
                        <div className={styles.fileLinkContainer}>
                          <a 
                            href={form.certifications_url} 
                            target="_blank" 
                            rel="noopener noreferrer"
                            className={styles.fileLink}
                          >
                            View Current Certifications
                          </a>
                        </div>
                      )}
                      <input
                        type="file"
                        name="certifications"
                        accept="application/pdf"
                        onChange={e => handleFileChange(e, 'certifications')}
                      />
                      {fileUploading.certifications && (
                        <span className={styles.uploading}>Uploading...</span>
                      )}
                      {(form.certifications || form.certifications_url) &&
                        !fileUploading.certifications && (
                          <span className={styles.uploaded}>✓ Uploaded</span>
                        )}
                    </>
                  )}
                </div>
              </div>
            </div>

            {/* Terms and Conditions - Only show in edit mode when not in read-only view */}
            {!isReadOnly && (
              <div className={styles.section}>
                <h3>Terms & Conditions</h3>
                <div className={styles.field}>
                  <label className={styles.termsLabel}>
                    <input
                      type="checkbox"
                      name="accepted_terms"
                      checked={form.accepted_terms || false}
                      onChange={handleChange}
                      required={!isEditMode}
                    />
                    I accept the terms and conditions *
                  </label>
                  {getFieldError("accepted_terms") && <span className={styles.fieldError}>{getFieldError("accepted_terms")}</span>}
                </div>
              </div>
            )}
          </div>
        </div>

        {/* Submit and Save Buttons */}
        {isEditMode && !isReadOnly && onCancel && onSave ? (
          <div className={styles.submitSection} style={{ justifyContent: 'flex-end', gap: '1rem' }}>
            <button
              type="button"
              onClick={onCancel}
              className={styles.cancelButton}
              disabled={saving}
            >
              Cancel
            </button>
            <button
              type="submit"
              className={hasFormChanges() ? styles.saveButton : styles.saveButtonInactive}
              disabled={saving || !hasFormChanges()}
            >
              {saving ? (
                <>
                  <span>Saving...</span>
                  <div className={styles.spinner}></div>
                </>
              ) : (
                'Save'
              )}
            </button>
          </div>
        ) : !isEditMode ? (
          <div className={styles.submitSection}>
            <button
              type="submit"
              className={styles.submitButton}
              disabled={saving || !form.accepted_terms}
            >
              {saving && !saved ? (
                <>
                  <span>Submitting...</span>
                  <div className={styles.spinner}></div>
                </>
              ) : (
                'Submit Onboarding Form'
              )}
            </button>
          </div>
        ) : null}
      </form>
    </div>
  );
}
